import React,
{
  useEffect, useMemo, useRef, useState,
} from "react";

import { CloseOutlined } from "@ant-design/icons";
import {
  Button, Input, Popover, Radio, Select, Checkbox,
} from "antd";
import moment from "moment/moment";
import { createPortal } from "react-dom";

import DatePicker from "../../../Common/DatePicker/DatePicker";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../../Common/UIComponents/SelectPopupScrollbar";
import SubmissionsFilterTags from "./SubmissionsFilterTags";

import { canUploadSampleSubmissionExcel } from "../../../../utils/Permissions";
import { getFormattedDaysAgoString } from "../../../Common/DatePicker/utils";

import { getSubmittedSubmissionsInfo } from "../../../../actions/sampleSubmission";

import { ReactComponent as CalendarTodoLineIcon } from "../../../../assets/icons/Sample Submission Redesign/calendar-todo-line.svg";
import { ReactComponent as FilterIcon } from "../../../../assets/icons/Sample Submission Redesign/filter-icon.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/icons/Sample Submission Redesign/search-line.svg";

import "./SubmissionsSearch.css";

const DEFAULT_SUBMISSIONS_SEARCH_BY = "all";

const FILTER_MODAL_SEARCH_BY_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Submission Name", value: "submission_name" },
  { label: "Sample ID", value: "sample_id" },
  { label: "PO", value: "po" },
];

const ISSUES_OPTIONS = [
  { label: "Has Issues", value: "yes" },
  { label: "No Issues", value: "no" },
];

const DELETED_SAMPLES_OPTIONS = [
  { label: "Deleted by you", value: "deleted_by_user" },
  { label: "Deleted by Lab", value: "deleted_by_lab" },
];

const STATUS_LABELS = {
  submitted: "Submitted",
  testing_in_progress: "Testing In Progress",
  testing_complete: "Testing Complete",
};

const CATEGORY_LABELS = {
  product: "Product",
  environment: "Environment",
};

const normalizeSearchScope = (value) => (
  FILTER_MODAL_SEARCH_BY_OPTIONS.some((opt) => opt.value === value)
    ? value
    : DEFAULT_SUBMISSIONS_SEARCH_BY
);

const countActiveFilters = ({
  searchBy = DEFAULT_SUBMISSIONS_SEARCH_BY,
  status = "",
  sampleCategory = "",
  issues = "",
  deletedSamples = [],
  dateFrom = "",
  dateTo = "",
  submittedBy = "",
} = {}) => {
  let count = 0;
  if (normalizeSearchScope(searchBy) !== DEFAULT_SUBMISSIONS_SEARCH_BY) count += 1;
  if (status) count += 1;
  if (sampleCategory) count += 1;
  if (issues) count += 1;
  count += deletedSamples.length;
  if (dateFrom && dateTo) count += 1;
  if (submittedBy) count += 1;
  return count;
};

const buildAppliedFilterTags = ({
  searchBy,
  status,
  sampleCategory,
  issues,
  deletedSamples,
  dateDisplay,
  submittedBy,
}) => {
  const tags = [];
  const normalizedSearchBy = normalizeSearchScope(searchBy);
  if (normalizedSearchBy !== DEFAULT_SUBMISSIONS_SEARCH_BY) {
    const searchLabel = FILTER_MODAL_SEARCH_BY_OPTIONS.find((opt) => opt.value === normalizedSearchBy)?.label;
    if (searchLabel) {
      tags.push({
        id: "searchBy",
        label: searchLabel,
        iconType: "searchBy",
      });
    }
  }
  if (status) {
    tags.push({
      id: "status",
      label: STATUS_LABELS[status] || status,
      iconType: "status",
    });
  }
  if (sampleCategory) {
    tags.push({
      id: "category",
      label: CATEGORY_LABELS[sampleCategory] || sampleCategory,
      iconType: "category",
    });
  }
  if (issues) {
    const issueLabel = ISSUES_OPTIONS.find((opt) => opt.value === issues)?.label;
    if (issueLabel) {
      tags.push({
        id: "issues",
        label: issueLabel,
        iconType: "issues",
      });
    }
  }
  deletedSamples.forEach((value) => {
    const deletedLabel = DELETED_SAMPLES_OPTIONS.find((opt) => opt.value === value)?.label;
    if (deletedLabel) {
      tags.push({
        id: `deleted:${value}`,
        label: deletedLabel,
        iconType: "deleted",
      });
    }
  });
  if (dateDisplay) {
    tags.push({
      id: "date",
      label: dateDisplay,
      iconType: "date",
    });
  }
  if (submittedBy) {
    tags.push({
      id: "submittedBy",
      label: submittedBy,
      iconType: "submittedBy",
    });
  }
  return tags;
};

const SEARCH_PLACEHOLDER_BY_SCOPE = {
  all: "Search anything...",
  submission_name: "Search Submission Name",
  sample_id: "Search Sample ID",
  po: "Search PO",
};

export default function SubmissionsSearch({
  getSubmissions,
  search,
  setSearch,
  dateFilter,
  setDateFilter,
  setSearchField,
  loadingState,
  setSubmitBy,
  setSampleFilterCategory,
  setStatusFilter,
  setIssuesFilter,
  setDeletedSamplesFilter,
  searchByField,
  submittedByLst = [],
  setShowUpdateOnboardingModal,
  filterTagsContainer,
}) {
  const [submittedBySearch, setSubmittedBySearch] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedIssues, setAppliedIssues] = useState("");
  const [appliedSubmissionTypes, setAppliedSubmissionTypes] = useState([]);
  const [appliedDeletedSamples, setAppliedDeletedSamples] = useState([]);
  const [draftStatus, setDraftStatus] = useState("");
  const [draftIssues, setDraftIssues] = useState("");
  const [draftSubmissionTypes, setDraftSubmissionTypes] = useState([]);
  const [draftDeletedSamples, setDraftDeletedSamples] = useState([]);
  const [draftSampleCategory, setDraftSampleCategory] = useState("");
  const [draftDateFrom, setDraftDateFrom] = useState(null);
  const [draftDateTo, setDraftDateTo] = useState(null);
  const [draftDateDisplay, setDraftDateDisplay] = useState("");
  const [draftDatePrevDisplay, setDraftDatePrevDisplay] = useState("");
  const [appliedSubmittedBy, setAppliedSubmittedBy] = useState("");
  const [draftSubmittedBy, setDraftSubmittedBy] = useState("");
  const [draftSearchBy, setDraftSearchBy] = useState(DEFAULT_SUBMISSIONS_SEARCH_BY);
  const searchRef = useRef();

  const submittedByOptions = useMemo(() => (
    (submittedByLst || [])
      .map((item) => {
        const value = typeof item === "string"
          ? item
          : (item?.submitted_by || item?.value || item?.user_name || "");
        if (!value) return null;
        return { label: value, value };
      })
      .filter(Boolean)
  ), [submittedByLst]);

  const getDates = async () => {
    const payload = {
      page: 1,
      submission_id: "",
      search_value: search,
      search_by: normalizeSearchScope(draftSearchBy),
      date_to: "",
      date_from: "",
      submitted_by: submittedBySearch,
      sample_category: filterCategory,
    };
    const { success, dates } = await getSubmittedSubmissionsInfo(payload);
    if (success) {
      return dates ?? [];
    }
    return [];
  };

  /**
   * Convert date range in local time to utc time.
   * @param {String} startDate date in local time (2023-12-12 format)
   * @param {String} endDate date in local time (2023-12-12 format)
   */
  const convertLocalDateRangetoUTC = (startDate, endDate) => {
    if (startDate || endDate) {
      const localStart = moment(startDate).startOf("day");
      const utcStart = moment(localStart).utc().format();
      let localEnd;
      if (endDate) {
        localEnd = moment(endDate).endOf("day");
      } else {
        localEnd = moment(startDate).endOf("day");
      }
      const utcEnd = moment(localEnd).utc().format();
      return [utcStart, utcEnd];
    }
    return [startDate, endDate]; // dates are empty
  };

  const buildDateFilterObj = (fromM, toM) => {
    if (fromM && toM) {
      let startM = fromM;
      let endM = toM;
      if (startM.isAfter(endM, "day")) {
        const t = startM;
        startM = endM;
        endM = t;
      }
      const startStr = startM.format("YYYY-MM-DD");
      const endStr = endM.format("YYYY-MM-DD");
      const [dateFromUTC, dateToUTC] = convertLocalDateRangetoUTC(startStr, endStr);
      return {
        dateFromUTC, dateToUTC, dateFromLocal: startStr, dateToLocal: endStr,
      };
    }
    return {
      dateFromUTC: "", dateToUTC: "", dateFromLocal: "", dateToLocal: "",
    };
  };

  const fetchSubmissions = ({
    status = appliedStatus,
    issues = appliedIssues,
    deletedSamples = appliedDeletedSamples,
    category = filterCategory,
    submittedBy = submittedBySearch,
    searchBy = searchByField,
    datesObj = dateFilter,
  } = {}) => {
    const normalizedSearchBy = normalizeSearchScope(searchBy);
    getSubmissions(
      1,
      search,
      normalizedSearchBy,
      datesObj,
      false,
      submittedBy,
      normalizedSearchBy,
      category,
      status,
      issues,
      deletedSamples,
    );
  };

  /**
   * Handle search
   * @param {String} value
   * setState of search value
   */
  const handleSearch = (value) => {
    const scope = normalizeSearchScope(searchByField);
    getSubmissions(1, value, scope, dateFilter, false, submittedBySearch, scope, filterCategory, appliedStatus, appliedIssues, appliedDeletedSamples);
    setSearch(value);
    setSearchField(scope);
  };

  /**
   * Handler for search box to start search when Enter is pressed
   * @param {Event} e
   */
  const handleSearchOnEnter = (e) => {
    if (e.key === "Enter") {
      handleSearch(e.target.value);
    }
  };

  const syncDraftFromApplied = () => {
    setDraftStatus(appliedStatus);
    setDraftIssues(appliedIssues);
    setDraftSubmissionTypes([...appliedSubmissionTypes]);
    setDraftDeletedSamples([...appliedDeletedSamples]);
    if (filterCategory === "environment") {
      setDraftSampleCategory("environment");
    } else if (filterCategory === "product") {
      setDraftSampleCategory("product");
    } else {
      setDraftSampleCategory("");
    }
    if (dateFilter.dateFromLocal && dateFilter.dateToLocal) {
      const fromM = moment(dateFilter.dateFromLocal);
      const toM = moment(dateFilter.dateToLocal);
      setDraftDateFrom(fromM);
      setDraftDateTo(toM);
      const label = getFormattedDaysAgoString(fromM.toDate(), toM.toDate());
      setDraftDateDisplay(label);
      setDraftDatePrevDisplay(label);
    } else {
      setDraftDateFrom(null);
      setDraftDateTo(null);
      setDraftDateDisplay("");
      setDraftDatePrevDisplay("");
    }
    setDraftSubmittedBy(appliedSubmittedBy);
    setDraftSearchBy(normalizeSearchScope(searchByField));
  };
  /**
   * State of submittedBySearch field in parent
   */
  useEffect(() => {
    setSubmitBy(submittedBySearch);
  }, [submittedBySearch]); // eslint-disable-line

  /**
   * State of search field in parent
   */
  useEffect(() => {
    setSearchField(searchByField);
  }, [searchByField]); // eslint-disable-line

  const handleQuickDateChange = (rangeType) => {
    const endDate = moment();
    let startDate = moment();
    if (rangeType === "last_7_days") startDate = moment().subtract(6, "days");
    if (rangeType === "last_30_days") startDate = moment().subtract(29, "days");
    if (rangeType === "this_month") startDate = moment().startOf("month");
    setDraftDateFrom(startDate);
    setDraftDateTo(endDate);
    const label = getFormattedDaysAgoString(startDate.toDate(), endDate.toDate());
    setDraftDateDisplay(label);
    setDraftDatePrevDisplay(label);
  };

  const handleDraftCalendarApply = (startStr, endStr) => {
    if (!startStr && !endStr) {
      setDraftDateFrom(null);
      setDraftDateTo(null);
      setDraftDateDisplay("");
      setDraftDatePrevDisplay("");
      return;
    }
    const startM = moment(startStr, "YYYY-MM-DD", true);
    if (!startM.isValid()) return;
    if (!endStr) {
      setDraftDateFrom(startM);
      setDraftDateTo(startM);
      return;
    }
    const endM = moment(endStr, "YYYY-MM-DD", true);
    if (!endM.isValid()) return;
    setDraftDateFrom(startM);
    setDraftDateTo(endM);
  };

  const handleApplyFilters = () => {
    let category = "";
    if (draftSampleCategory === "environment") {
      category = "environment";
    } else if (draftSampleCategory === "product") {
      category = "product";
    }

    const dateFilterObj = buildDateFilterObj(draftDateFrom, draftDateTo);

    const submittedBy = draftSubmittedBy || "";
    const normalizedSearchBy = normalizeSearchScope(draftSearchBy);
    getSubmissions(
      1,
      search,
      normalizedSearchBy,
      dateFilterObj,
      false,
      submittedBy,
      normalizedSearchBy,
      category,
      draftStatus,
      draftIssues,
      draftDeletedSamples,
    );

    setSearchField(normalizedSearchBy);
    setSubmittedBySearch(submittedBy);
    setDateFilter(dateFilterObj);
    setFilterCategory(category);
    setSampleFilterCategory(category);
    if (typeof setStatusFilter === "function") {
      setStatusFilter(draftStatus);
    }
    if (typeof setIssuesFilter === "function") {
      setIssuesFilter(draftIssues);
    }
    if (typeof setDeletedSamplesFilter === "function") {
      setDeletedSamplesFilter([...draftDeletedSamples]);
    }

    setAppliedStatus(draftStatus);
    setAppliedIssues(draftIssues);
    setAppliedSubmissionTypes([...draftSubmissionTypes]);
    setAppliedDeletedSamples([...draftDeletedSamples]);
    setAppliedSubmittedBy(submittedBy);

    setShowFilterModal(false);
  };

  const handleCancelFilters = () => {
    setShowFilterModal(false);
  };

  const handleClearFilters = () => {
    setDraftStatus("");
    setDraftIssues("");
    setDraftSubmissionTypes([]);
    setDraftDeletedSamples([]);
    setDraftSampleCategory("");
    setDraftDateFrom(null);
    setDraftDateTo(null);
    setDraftDateDisplay("");
    setDraftDatePrevDisplay("");
    setDraftSubmittedBy("");
    setDraftSearchBy(DEFAULT_SUBMISSIONS_SEARCH_BY);
  };

  const clearAllAppliedFilters = () => {
    const emptyDateFilter = {
      dateFromUTC: "", dateToUTC: "", dateFromLocal: "", dateToLocal: "",
    };

    setAppliedStatus("");
    setAppliedIssues("");
    setAppliedSubmissionTypes([]);
    setAppliedDeletedSamples([]);
    setAppliedSubmittedBy("");
    setFilterCategory("");
    setDateFilter(emptyDateFilter);
    setSubmittedBySearch("");
    setSearchField(DEFAULT_SUBMISSIONS_SEARCH_BY);

    if (typeof setStatusFilter === "function") setStatusFilter("");
    if (typeof setIssuesFilter === "function") setIssuesFilter("");
    if (typeof setDeletedSamplesFilter === "function") setDeletedSamplesFilter([]);
    if (typeof setSampleFilterCategory === "function") setSampleFilterCategory("");

    getSubmissions(
      1,
      search,
      DEFAULT_SUBMISSIONS_SEARCH_BY,
      emptyDateFilter,
      false,
      "",
      DEFAULT_SUBMISSIONS_SEARCH_BY,
      "",
      "",
      "",
      [],
    );
  };

  const handleRemoveFilterTag = (tagId) => {
    if (tagId === "searchBy") {
      setSearchField(DEFAULT_SUBMISSIONS_SEARCH_BY);
      fetchSubmissions({ searchBy: DEFAULT_SUBMISSIONS_SEARCH_BY });
      return;
    }

    if (tagId === "status") {
      setAppliedStatus("");
      if (typeof setStatusFilter === "function") setStatusFilter("");
      fetchSubmissions({ status: "" });
      return;
    }

    if (tagId === "category") {
      setFilterCategory("");
      if (typeof setSampleFilterCategory === "function") setSampleFilterCategory("");
      fetchSubmissions({ category: "" });
      return;
    }

    if (tagId === "issues") {
      setAppliedIssues("");
      if (typeof setIssuesFilter === "function") setIssuesFilter("");
      fetchSubmissions({ issues: "" });
      return;
    }

    if (tagId.startsWith("deleted:")) {
      const deletedValue = tagId.replace("deleted:", "");
      const nextDeletedSamples = appliedDeletedSamples.filter((value) => value !== deletedValue);
      setAppliedDeletedSamples(nextDeletedSamples);
      if (typeof setDeletedSamplesFilter === "function") setDeletedSamplesFilter(nextDeletedSamples);
      fetchSubmissions({ deletedSamples: nextDeletedSamples });
      return;
    }

    if (tagId === "date") {
      const emptyDateFilter = {
        dateFromUTC: "", dateToUTC: "", dateFromLocal: "", dateToLocal: "",
      };
      setDateFilter(emptyDateFilter);
      fetchSubmissions({ datesObj: emptyDateFilter });
      return;
    }

    if (tagId === "submittedBy") {
      setAppliedSubmittedBy("");
      setSubmittedBySearch("");
      fetchSubmissions({ submittedBy: "" });
    }
  };

  const dateFromLocal = dateFilter?.dateFromLocal ?? "";
  const dateToLocal = dateFilter?.dateToLocal ?? "";
  const appliedDateDisplay = (dateFromLocal && dateToLocal)
    ? getFormattedDaysAgoString(moment(dateFromLocal).toDate(), moment(dateToLocal).toDate())
    : "";

  const appliedFilterCount = useMemo(() => countActiveFilters({
    searchBy: searchByField,
    status: appliedStatus,
    sampleCategory: filterCategory,
    issues: appliedIssues,
    deletedSamples: appliedDeletedSamples,
    dateFrom: dateFromLocal,
    dateTo: dateToLocal,
    submittedBy: appliedSubmittedBy,
  }), [
    searchByField,
    appliedStatus,
    filterCategory,
    appliedIssues,
    appliedDeletedSamples,
    dateFromLocal,
    dateToLocal,
    appliedSubmittedBy,
  ]);

  // const draftFilterCount = useMemo(() => countActiveFilters({
  //   searchBy: draftSearchBy,
  //   status: draftStatus,
  //   sampleCategory: draftSampleCategory,
  //   issues: draftIssues,
  //   deletedSamples: draftDeletedSamples,
  //   dateFrom: draftDateFrom ? draftDateFrom.format("YYYY-MM-DD") : "",
  //   dateTo: draftDateTo ? draftDateTo.format("YYYY-MM-DD") : "",
  //   submittedBy: draftSubmittedBy,
  // }), [
  //   draftSearchBy,
  //   draftStatus,
  //   draftSampleCategory,
  //   draftIssues,
  //   draftDeletedSamples,
  //   draftDateFrom,
  //   draftDateTo,
  //   draftSubmittedBy,
  // ]);

  const appliedFilterTags = useMemo(() => buildAppliedFilterTags({
    searchBy: searchByField,
    status: appliedStatus,
    sampleCategory: filterCategory,
    issues: appliedIssues,
    deletedSamples: appliedDeletedSamples,
    dateDisplay: appliedDateDisplay,
    submittedBy: appliedSubmittedBy,
  }), [
    searchByField,
    appliedStatus,
    filterCategory,
    appliedIssues,
    appliedDeletedSamples,
    appliedDateDisplay,
    appliedSubmittedBy,
  ]);

  const searchPlaceholder = SEARCH_PLACEHOLDER_BY_SCOPE[normalizeSearchScope(searchByField)];
  const hasAppliedFilters = appliedFilterCount > 0;
  // const modalFilterCount = showFilterModal ? draftFilterCount : appliedFilterCount;

  return (
    <>
      <div className="SubmissionsSearch">
        <div className="SubmissionsSearch__Filters_SearchDiv_Filters">
          <div className="SubmissionSearch__Filters_SearchDiv">
            <Input
              type="search"
              value={search}
              onChange={(e) => {
                const v = e.target.value;
                setSearch(v);
                if (!v) handleSearch("");
              }}
              onKeyUp={handleSearchOnEnter}
              className="SubmissionsSearch__Input"
              size="large"
              placeholder={searchPlaceholder}
              ref={searchRef}
              disabled={loadingState}
              allowClear
              suffix={(
                <button
                  type="button"
                  className="SubmissionsSearch__InputSuffixIcon SubmissionsSearch__InputSuffixButton"
                  aria-label="Search submissions"
                  disabled={loadingState}
                  onClick={() => handleSearch(search)}
                >
                  <SearchIcon aria-hidden />
                </button>
            )}
            />
          </div>
          <Popover
            open={showFilterModal}
            onOpenChange={(open) => {
              if (open) {
                syncDraftFromApplied();
              }
              setShowFilterModal(open);
            }}
            trigger="click"
            placement="bottomLeft"
            autoAdjustOverflow={false}
            arrow={false}
            overlayClassName="SubmissionsSearch__FilterModal"
            content={(
              <div
                className="SubmissionsSearch__FilterModalContent"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="SubmissionsSearch__FilterModalHeader">
                  <h3>
                    Filter
                    {/* {modalFilterCount > 0 && (
                    <span className="SubmissionsSearch__FilterModalCount">{modalFilterCount}</span>
                  )} */}
                  </h3>
                  <button
                    type="button"
                    className="SubmissionsSearch__FilterModalClose"
                    onClick={handleCancelFilters}
                    aria-label="Close filter"
                  >
                    <CloseOutlined />
                  </button>
                </div>
                <StyledScrollbar
                  className="SubmissionsSearch__FilterModalScrollbar"
                  noScrollX
                >
                  <div className="SubmissionsSearch__FilterModalBody">
                    <div className="SubmissionsSearch__ModalSection">
                      <p className="SubmissionsSearch__ModalLabel">Search By</p>
                      <Radio.Group
                        className="SubmissionsSearch__RadioGroup SubmissionsSearch__SearchByRadioGroup"
                        name="submissions-search-by"
                        value={draftSearchBy}
                        aria-label="Search by"
                      >
                        {FILTER_MODAL_SEARCH_BY_OPTIONS.map((opt) => (
                          <div
                            key={opt.value}
                            role="button"
                            tabIndex={0}
                            className={`SubmissionsSearch__RadioOption${draftSearchBy === opt.value ? " SubmissionsSearch__RadioOption--checked" : ""}`}
                            onClick={() => setDraftSearchBy(opt.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setDraftSearchBy(opt.value);
                              }
                            }}
                          >
                            <Radio value={opt.value}>{opt.label}</Radio>
                          </div>
                        ))}
                      </Radio.Group>
                    </div>
                    <div className="SubmissionsSearch__ModalSection">
                      <p className="SubmissionsSearch__ModalLabel">Status</p>
                      <div className="SubmissionsSearch__StatusButtons">
                        {[
                          { label: "Submitted", value: "submitted" },
                          { label: "Testing In Progress", value: "testing_in_progress" },
                          { label: "Testing Complete", value: "testing_complete" },
                        ].map((status) => (
                          <button
                            key={status.value}
                            type="button"
                            className={`SubmissionsSearch__ChipButton ${draftStatus === status.value ? "SubmissionsSearch__ChipButton--active" : ""}`}
                            onClick={() => setDraftStatus((s) => (s === status.value ? "" : status.value))}
                          >
                            {status.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="SubmissionsSearch__ModalSection">
                      <p className="SubmissionsSearch__ModalLabel">Sample Category</p>
                      <div className="SubmissionsSearch__SampleCategoryButtons">
                        {[
                          { label: "Product", value: "product" },
                          { label: "Environment", value: "environment" },
                        ].map((category) => (
                          <button
                            key={category.value}
                            type="button"
                            className={`SubmissionsSearch__ChipButton ${draftSampleCategory === category.value ? "SubmissionsSearch__ChipButton--active" : ""}`}
                            onClick={() => setDraftSampleCategory((c) => (c === category.value ? "" : category.value))}
                          >
                            {category.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="SubmissionsSearch__ModalSection">
                      <p className="SubmissionsSearch__ModalLabel">Issues</p>
                      <Radio.Group
                        className="SubmissionsSearch__RadioGroup SubmissionsSearch__IssuesRadioGroup"
                        name="submissions-search-issues"
                        value={draftIssues || undefined}
                        aria-label="Issues"
                      >
                        {ISSUES_OPTIONS.map((issue) => (
                          <div
                            key={issue.value}
                            role="button"
                            tabIndex={0}
                            className={`SubmissionsSearch__RadioOption${draftIssues === issue.value ? " SubmissionsSearch__RadioOption--checked" : ""}`}
                            onClick={() => setDraftIssues((current) => (current === issue.value ? "" : issue.value))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setDraftIssues((current) => (current === issue.value ? "" : issue.value));
                              }
                            }}
                          >
                            <Radio value={issue.value}>{issue.label}</Radio>
                          </div>
                        ))}
                      </Radio.Group>
                    </div>
                    {/*
                  <div className="SubmissionsSearch__ModalSection">
                    <p className="SubmissionsSearch__ModalLabel">Submission Type</p>
                    <Checkbox.Group
                      className="SubmissionsSearch__TypeGrid"
                      value={draftSubmissionTypes}
                      onChange={(values) => setDraftSubmissionTypes(values)}
                      options={[
                        { label: "Sample Submission", value: "sample_submission" },
                        { label: "Swabathon", value: "swabathon" },
                        { label: "Re-Test", value: "re_test" },
                        { label: "Corrective Action", value: "corrective_action" },
                      ]}
                    />
                  </div>
                  */}
                    <div className="SubmissionsSearch__ModalSection">
                      <p className="SubmissionsSearch__ModalLabel">Deleted Samples</p>
                      <Checkbox.Group
                        className="SubmissionsSearch__TypeGrid"
                        value={draftDeletedSamples}
                        onChange={(values) => setDraftDeletedSamples(values)}
                        options={DELETED_SAMPLES_OPTIONS}
                      />
                    </div>
                    <div className="SubmissionsSearch__ModalSection">
                      <p className="SubmissionsSearch__ModalLabel">Date Range</p>
                      <div className="SubmissionsSearch__FilterDateWrap">
                        <DatePicker
                          getReportDates={getDates}
                          value={draftDateDisplay}
                          onChange={setDraftDateDisplay}
                          prevValue={draftDatePrevDisplay}
                          setPrevValue={setDraftDatePrevDisplay}
                          handleSearchDate={handleDraftCalendarApply}
                          searchOnClear
                          allowFutureDateSelect
                          allowClear
                          getPopupContainer={(node) => (node?.closest?.(".SubmissionsSearch__FilterModalContent") || document.body)}
                          popoverClassName="SubmissionsSearch__CalendarPopover"
                          calendarClassName="SubmissionsSearch__FilterCalendar"
                          datePickerDisplayProps={{
                            displayTextPosition: "right",
                            displayClassName: "SubmissionsSearch__FilterDateDisplay",
                            customCalendarIcon: <CalendarTodoLineIcon width={16} height={16} aria-hidden />,
                            emptyPlaceholder: "DD-MM-YYYY",
                          }}
                        />
                      </div>
                      <div className="SubmissionsSearch__QuickRanges">
                        <button type="button" className="SubmissionsSearch__ChipButton" onClick={() => handleQuickDateChange("last_7_days")}>Last 7 days</button>
                        <button type="button" className="SubmissionsSearch__ChipButton" onClick={() => handleQuickDateChange("last_30_days")}>Last 30 days</button>
                        <button type="button" className="SubmissionsSearch__ChipButton" onClick={() => handleQuickDateChange("this_month")}>This month</button>
                      </div>
                    </div>
                    <div className="SubmissionsSearch__ModalSection">
                      <p className="SubmissionsSearch__ModalLabel">Submitted By</p>
                      <Select
                        value={draftSubmittedBy || undefined}
                        onChange={(value) => setDraftSubmittedBy(value || "")}
                        options={submittedByOptions}
                        placeholder="Select submitted by"
                        {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                        className="SubmissionsSearch__SubmittedByDropdown"
                        classNames={selectPopupClassNames("SubmissionsSearch__InputSelect__DropdownList")}
                        allowClear
                        virtual={false}
                        listHeight={256}
                        getPopupContainer={() => document.body}
                        popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                          optionCount: submittedByOptions.length,
                          className: "SubmissionsSearch__SubmittedByDropdownScroll",
                        })}
                      />
                    </div>
                  </div>
                </StyledScrollbar>
                <div className="SubmissionsSearch__ModalFooter">
                  <button
                    type="button"
                    className="SubmissionsSearch__ClearFiltersBtn"
                    onClick={handleClearFilters}
                  >
                    Clear filters
                  </button>
                  <div className="SubmissionsSearch__ModalFooterActions">
                    <Button
                      htmlType="button"
                      className="SubmissionsSearch__ModalBtn SubmissionsSearch__ModalBtn--secondary"
                      onClick={handleCancelFilters}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      htmlType="button"
                      className="SubmissionsSearch__ModalBtn SubmissionsSearch__ModalBtn--primary"
                      onClick={handleApplyFilters}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
          )}
          >
            <button
              type="button"
              className={[
                "SubmissionsSearch__FilterToggle",
                showFilterModal ? "SubmissionsSearch__FilterToggle--active" : "",
                hasAppliedFilters ? "SubmissionsSearch__FilterToggle--hasFilters" : "",
              ].filter(Boolean).join(" ")}
              disabled={loadingState && !showFilterModal}
              aria-label={hasAppliedFilters ? `Filter, ${appliedFilterCount} selected` : "Filter submissions"}
            >
              <FilterIcon />
              <span className="SubmissionsSearch__FilterToggleLabel">Filter</span>
              {hasAppliedFilters && (
                <span className="SubmissionsSearch__FilterToggleCount">{appliedFilterCount}</span>
              )}
            </button>
          </Popover>
          {canUploadSampleSubmissionExcel() && (
            <Button
              type="text"
              htmlType="button"
              className="UploadExcelButton"
              onClick={() => setShowUpdateOnboardingModal(true)}
            >
              Upload Excel
            </Button>
          )}
          {/* <span className="SubmissionsSearch__TestDirectoryButton SubmissionsSearch__TestDirectoryButton--static">
          <TubeIcon width={14} height={14} aria-hidden />
          <span>Test Directory</span>
        </span> */}
        </div>
      </div>
      {filterTagsContainer && createPortal(
        <SubmissionsFilterTags
          tags={appliedFilterTags}
          onRemoveTag={handleRemoveFilterTag}
          onClearAll={clearAllAppliedFilters}
        />,
        filterTagsContainer,
      )}
    </>
  );
}
